import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { InquiryService } from '../services/inquiry.service';
import { ArrowLeft, Mail, CheckCircle, Clock } from 'lucide-react';
import toast from 'react-hot-toast';

export default function InquiryDetails() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [inquiry, setInquiry] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [replyMessage, setReplyMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const fetchInquiry = async () => {
      try {
        const res = await InquiryService.getInquiry(id);
        const data = res?.data || res;
        setInquiry(data);
      } catch (error) {
        toast.error('Failed to load inquiry details');
        navigate('/inqueries');
      } finally {
        setIsLoading(false);
      }
    };

    if (id) {
      fetchInquiry();
    }
  }, [id, navigate]);

  const handleSendReply = async () => {
    if (!replyMessage.trim()) {
      toast.error('Please enter a proper solution or reply');
      return;
    }

    setIsSubmitting(true);
    try {
      // Send the reply message to backend where the email will be processed
      await InquiryService.updateInquiry(id, {
        status: 'Resolved',
        replyMessage
      });

      toast.success('Reply sent and inquiry resolved successfully!');

      // Update local state
      setInquiry(prev => ({ ...prev, status: 'Resolved' }));
      setReplyMessage('');
    } catch (error) {
      toast.error('Failed to send reply');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return <div className="page-content"><p>Loading inquiry details...</p></div>;
  }

  if (!inquiry) {
    return <div className="page-content"><p>Inquiry not found.</p></div>;
  }

  const isResolved = inquiry.status === 'Resolved';

  return (
    <div className="page-content">
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
        <button
          onClick={() => navigate('/inqueries')}
          style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', color: 'var(--text-secondary)' }}
        >
          <ArrowLeft size={20} />
        </button>
        <h2 style={{ fontSize: '24px', fontWeight: '600', margin: 0 }}>Inquiry Details</h2>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
        {/* Left Column: Inquiry Info */}
        <div style={{ background: 'white', borderRadius: '12px', padding: '24px', border: '1px solid var(--border-color)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
            <div>
              <h3 style={{ fontSize: '18px', fontWeight: '600', margin: '0 0 4px' }}>{inquiry.name}</h3>
              <p style={{ color: 'var(--text-secondary)', margin: 0, fontSize: '14px' }}>{inquiry.email} • {inquiry.mobile_number || inquiry.phone}</p>
            </div>
            <div style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              padding: '6px 12px', borderRadius: '20px', fontSize: '13px', fontWeight: '500',
              background: isResolved ? '#e8f9ed' : '#fff4e6',
              color: isResolved ? '#28A745' : '#FF9800'
            }}>
              {isResolved ? <CheckCircle size={14} /> : <Clock size={14} />}
              {isResolved ? 'Resolved' : 'Pending'}
            </div>
          </div>

          <div style={{ background: '#F8F9FB', padding: '16px', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
            <p style={{ fontSize: '13px', color: 'var(--text-secondary)', margin: '0 0 8px', fontWeight: '600' }}>MESSAGE</p>
            <p style={{ margin: 0, fontSize: '15px', color: 'var(--text-primary)', lineHeight: '1.5' }}>
              {inquiry.message}
            </p>
          </div>

          <div style={{ marginTop: '20px', fontSize: '13px', color: 'var(--text-secondary)' }}>
            Received on: {inquiry.createdAt ? new Date(inquiry.createdAt).toLocaleString() : inquiry.date}
          </div>
        </div>

        {/* Right Column: Resolution Form */}
        <div style={{ background: 'white', borderRadius: '12px', padding: '24px', border: '1px solid var(--border-color)', display: 'flex', flexDirection: 'column' }}>
          <h3 style={{ fontSize: '18px', fontWeight: '600', margin: '0 0 16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Mail size={20} color="var(--primary-color)" /> Resolve & Reply
          </h3>

          {isResolved ? (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: 'var(--text-secondary)', textAlign: 'center', padding: '40px' }}>
              <CheckCircle size={48} color="#28A745" style={{ marginBottom: '16px' }} />
              <p style={{ fontSize: '16px', fontWeight: '500', margin: '0 0 8px' }}>This inquiry is resolved.</p>
              <p style={{ fontSize: '14px', margin: 0 }}>An email response has already been sent to the user.</p>
            </div>
          ) : (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <p style={{ fontSize: '14px', color: 'var(--text-secondary)', marginBottom: '16px' }}>
                Write a proper solution to this inquiry. When you submit, an email will be automatically sent to <strong>{inquiry.email}</strong> from the backend.
              </p>

              <textarea
                placeholder="Type your official reply here..."
                value={replyMessage}
                onChange={(e) => setReplyMessage(e.target.value)}
                style={{
                  flex: 1, width: '100%', minHeight: '200px', padding: '16px',
                  borderRadius: '8px', border: '1px solid var(--border-color)',
                  fontSize: '14px', resize: 'vertical', fontFamily: 'inherit',
                  marginBottom: '16px'
                }}
              />

              <button
                onClick={handleSendReply}
                disabled={isSubmitting}
                style={{
                  width: '100%', padding: '14px', borderRadius: '8px',
                  background: '#e6620bff', color: 'white',
                  border: 'none', fontWeight: '600', fontSize: '15px',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  opacity: isSubmitting ? 0.7 : 1,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                  transition: 'background 0.2s'
                }}
              >
                {isSubmitting ? 'Sending...' : (
                  <>
                    <Mail size={18} /> Send Email & Resolve
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
